import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, Info, Search } from 'lucide-react';
import Calendar from '../../../core/components/dashboard/Calendar/Calendar';
import { getCouponService } from '../../../domains/coupon';
import { getPointService } from '../../../domains/point';
import { useCouponsByPharmacy } from '../../hooks/coupon/useCouponsByPharmacy';
import { usePointDistributions } from '../../hooks/point/usePointDistributions';
import { usePagination } from '../../hooks/ui/usePagination';
import { useOutsideClick } from '../../hooks/ui/useOutsideClick';
import useAuthStore from '../../../core/stores/authStore';
import {
  formatAmount,
} from '../shared/dummyData';
import useUIStore from '../../../core/stores/uiStore';
import SummaryCards from '../../../core/components/dashboard/SummaryCards/SummaryCards';
import styles from './EventDistributions.module.css';

// Figma 15:7611 — 배경칩 없이 텍스트 컬러만. Accent Blue / Green-1 토큰 사용
const DISCOUNT_TYPE_CONFIG = {
  FIXED: { label: '고정금액', color: '#007AFF', bg: 'transparent' },
  PERCENT: { label: '비율할인', color: '#34C759', bg: 'transparent' },
};

const DISCOUNT_TYPE_LABELS = {
  FIXED: '고정금액',
  PERCENT: '비율할인',
};

const SCOPE_TYPE_LABELS = {
  PHARMACY: '우리 약국',
  PHARM_CODE: '지역 그룹',
  BRANCH_CODE: '브랜치 그룹',
  ALL: '전체',
};

const GENDER_LABELS = {
  ALL: '전체',
  MALE: '남성',
  FEMALE: '여성',
};

const POINT_TYPE_CONFIG = {
  EARN: { label: '적립', color: '#2E7D32', bg: '#E8F5E9' },
  USE: { label: '사용', color: '#1565C0', bg: '#E3F2FD' },
  CANCEL: { label: '취소', color: '#C62828', bg: '#FFEBEE' },
  EXPIRE: { label: '만료', color: '#E65100', bg: '#FFF3E0' },
};

const EVENT_TYPE_TABS = [
  { id: 'coupon', label: '쿠폰 이벤트' },
  { id: 'point', label: '포인트 이벤트' },
];

const COUPON_SUB_TABS = [
  { key: 'all', label: '전체' },
  { key: 'FIXED', label: '고정금액' },
  { key: 'PERCENT', label: '비율할인' },
];

const POINT_SUB_TABS = [
  { key: 'all', label: '전체' },
  { key: 'EARN', label: '적립' },
  { key: 'CANCEL', label: '취소' },
];

const ITEMS_PER_PAGE = 10;

const PERIOD_OPTIONS = [
  { key: 'yesterday', label: '어제' },
  { key: 'lastWeek', label: '지난주' },
  { key: 'lastMonth', label: '지난달' },
  { key: 'thisMonth', label: '이번 달' },
];

const fmtDate = d => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getDateRangeForPeriod = key => {
  const today = new Date();
  let start, end;
  switch (key) {
    case 'yesterday': {
      const d = new Date(today);
      d.setDate(d.getDate() - 1);
      start = d;
      end = new Date(d);
      break;
    }
    case 'lastWeek': {
      const d = new Date(today);
      const dayOfWeek = d.getDay();
      end = new Date(d);
      end.setDate(d.getDate() - dayOfWeek - 1);
      start = new Date(end);
      start.setDate(end.getDate() - 6);
      break;
    }
    case 'lastMonth': {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    }
    case 'thisMonth': {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
      break;
    }
    default: {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    }
  }
  return { start, end };
};

const formatTargetAges = (ages) => {
  if (!ages || ages.length === 0) return '전체';
  return ages.map(a => `${a}대`).join(', ');
};

const getTargetDisplay = (coupon) => {
  const gender = GENDER_LABELS[coupon.target_gender] || '전체';
  const ages = formatTargetAges(coupon.target_ages);
  if (gender === '전체' && ages === '전체') return '전체';
  return `${gender} / ${ages}`;
};

const EventDistributions = () => {
  const pharmacy = useAuthStore(state => state.pharmacy);
  const setHeaderTabs = useUIStore(state => state.setHeaderTabs);

  const [eventType, setEventType] = useState('coupon');
  const [subFilter, setSubFilter] = useState('all');

  // Coupon listing → hook
  const couponsQuery = useCouponsByPharmacy(pharmacy?.id, { limit: 100, offset: 0 });
  const coupons = couponsQuery.data?.items ?? [];
  const isLoading = couponsQuery.isLoading;
  const totalCouponCount = couponsQuery.data?.total ?? 0;
  const [selectedItem, setSelectedItem] = useState(null);

  // Shared filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [periodFilter, setPeriodFilter] = useState('thisMonth');
  const [dateRange, setDateRange] = useState(() => getDateRangeForPeriod('thisMonth'));
  const [calendarDraft, setCalendarDraft] = useState(dateRange);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef(null);

  // Point data → hook (eventType=point일 때만 호출)
  const pointParams = useMemo(() => ({
    transaction_type: 'EARN',
    start_date: fmtDate(dateRange.start),
    end_date: fmtDate(dateRange.end),
    limit: 200,
    offset: 0,
  }), [dateRange]);
  const pointQuery = usePointDistributions(
    pharmacy?.business_registration_number,
    pointParams,
    eventType === 'point'
  );
  const pointDistributions = pointQuery.data?.items ?? [];
  const isPointLoading = pointQuery.isLoading;
  const pointTotal = pointQuery.data?.total ?? 0;
  const pointSummary = pointQuery.data?.summary ?? {};

  // Current sub tabs
  const currentSubTabs = eventType === 'coupon' ? COUPON_SUB_TABS : POINT_SUB_TABS;

  // Header tabs
  useEffect(() => {
    setHeaderTabs(
      EVENT_TYPE_TABS,
      eventType,
      (id) => setEventType(id)
    );
    return () => setHeaderTabs(null);
  }, [setHeaderTabs, eventType]);

  // 새로고침 wrapper (mutation 후 호출용)
  const refreshCoupons = useCallback(() => couponsQuery.refetch(), [couponsQuery]);
  const fetchPointDistributions = useCallback(() => pointQuery.refetch(), [pointQuery]);

  // Reset subFilter when eventType changes
  useEffect(() => {
    setSubFilter('all');
    setSearchQuery('');
    setSearchInput('');
  }, [eventType]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [eventType, subFilter, searchQuery, periodFilter, dateRange]);

  // Calendar outside click (hook)
  useOutsideClick(calendarRef, () => setIsCalendarOpen(false), isCalendarOpen);

  // Filtered coupon data
  const filteredCoupons = useMemo(() => {
    let filtered = [...coupons];

    if (subFilter !== 'all') {
      filtered = filtered.filter(c => c.discount_type === subFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        (c.description || '').toLowerCase().includes(q) ||
        getTargetDisplay(c).toLowerCase().includes(q)
      );
    }

    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter(c => {
        const dateStr = c.created_at || c.expiry_date;
        if (!dateStr) return true;
        const date = new Date(dateStr);
        return date >= dateRange.start && date <= new Date(dateRange.end.getTime() + 86400000);
      });
    }

    return filtered;
  }, [coupons, subFilter, searchQuery, dateRange]);

  // Filtered point data
  const filteredPoints = useMemo(() => {
    let filtered = [...pointDistributions];

    if (subFilter !== 'all') {
      filtered = filtered.filter(d => d.transaction_type === subFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(d =>
        (d.description || '').toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [pointDistributions, subFilter, searchQuery]);

  const currentFilteredData = eventType === 'coupon' ? filteredCoupons : filteredPoints;

  // KPI - 3 summary cards
  const kpiItems = useMemo(() => {
    if (eventType === 'coupon') {
      const fixedCount = coupons.filter(c => c.discount_type === 'FIXED').length;
      const percentCount = coupons.filter(c => c.discount_type === 'PERCENT').length;
      return [
        { label: '전체', value: `${coupons.length}건` },
        { label: '고정금액', value: `${fixedCount}건` },
        { label: '퍼센트할인', value: `${percentCount}건` },
      ];
    } else {
      const total = pointDistributions.length;
      const totalAmount = pointDistributions.reduce((sum, d) => sum + (d.amount || 0), 0);
      const earnCount = pointDistributions.filter(d => d.transaction_type === 'EARN').length;
      return [
        { label: '총 지급 건수', value: `${total}건` },
        { label: '총 지급 포인트', value: `${formatAmount(totalAmount)}P` },
        { label: '적립 건수', value: `${earnCount}건` },
      ];
    }
  }, [eventType, coupons, pointDistributions]);

  // Pagination (hook)
  const pagination = usePagination({ totalItems: currentFilteredData.length, pageSize: ITEMS_PER_PAGE });
  const { currentPage, setCurrentPage, totalPages } = pagination;
  const paginatedData = pagination.sliceItems(currentFilteredData);

  const formatCouponAmount = (coupon) => {
    if (coupon.discount_type === 'PERCENT') return `${coupon.amount}%`;
    return `${Number(coupon.amount || 0).toLocaleString()}원`;
  };

  const handlePeriodChange = key => {
    setPeriodFilter(key);
    setDateRange(getDateRangeForPeriod(key));
  };

  const handleCalendarChange = val => {
    if (val.start && val.end) {
      setPeriodFilter('');
      setDateRange({ start: val.start, end: val.end });
      setIsCalendarOpen(false);
    } else if (val.start) {
      setDateRange(prev => ({ ...prev, start: val.start }));
    }
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const renderCouponTable = () => (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>쿠폰 ID</th>
          <th>타겟 대상</th>
          <th>할인 방식</th>
          <th>금액/비율</th>
          <th>범위</th>
          <th>설명</th>
          <th>만료일</th>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td colSpan={7} className={styles.emptyState}>
              <div className={styles.emptyText}>로딩 중...</div>
            </td>
          </tr>
        ) : paginatedData.length > 0 ? (
          paginatedData.map((coupon, idx) => {
            const dtConfig = DISCOUNT_TYPE_CONFIG[coupon.discount_type] || DISCOUNT_TYPE_CONFIG.FIXED;
            return (
              <tr
                key={coupon.coupon_id || coupon.id || idx}
                className={styles.clickableRow}
                onClick={() => setSelectedItem({ type: 'coupon', data: coupon })}
              >
                <td className={styles.couponId}>#{coupon.coupon_id || coupon.id || '-'}</td>
                <td>{getTargetDisplay(coupon)}</td>
                <td>
                  <span
                    className={styles.typeBadge}
                    style={{ backgroundColor: dtConfig.bg, color: dtConfig.color }}
                  >
                    {dtConfig.label}
                  </span>
                </td>
                <td className={styles.amount}>{formatCouponAmount(coupon)}</td>
                <td>{SCOPE_TYPE_LABELS[coupon.scope_type] || coupon.scope_type || '-'}</td>
                <td>{coupon.description || '-'}</td>
                <td>{coupon.expiry_date ? new Date(coupon.expiry_date).toLocaleDateString('ko-KR') : '-'}</td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan={7} className={styles.emptyState}>
              <div className={styles.emptyText}>지급된 쿠폰이 없습니다</div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  const renderPointTable = () => (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>일시</th>
          <th>유형</th>
          <th>금액</th>
          <th>설명</th>
          <th>승인번호</th>
        </tr>
      </thead>
      <tbody>
        {isPointLoading ? (
          <tr>
            <td colSpan={5} className={styles.emptyState}>
              <div className={styles.emptyText}>로딩 중...</div>
            </td>
          </tr>
        ) : paginatedData.length > 0 ? (
          paginatedData.map((item, idx) => {
            const typeCfg = POINT_TYPE_CONFIG[item.transaction_type] || POINT_TYPE_CONFIG.EARN;
            return (
              <tr
                key={item.id || idx}
                className={styles.clickableRow}
                onClick={() => setSelectedItem({ type: 'point', data: item })}
              >
                <td>{item.created_at ? new Date(item.created_at).toLocaleDateString('ko-KR') : '-'}</td>
                <td>
                  <span
                    className={styles.typeBadge}
                    style={{ backgroundColor: typeCfg.bg, color: typeCfg.color }}
                  >
                    {typeCfg.label}
                  </span>
                </td>
                <td className={styles.amount}>{formatAmount(item.amount || 0)}P</td>
                <td>{item.description || '-'}</td>
                <td>{item.approval_number || '-'}</td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan={5} className={styles.emptyState}>
              <div className={styles.emptyText}>지급 내역이 없습니다</div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  const renderCouponModal = (coupon) => (
    <div className={styles.detailGrid}>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>쿠폰 ID</div>
        <div className={styles.detailValue}>#{coupon.coupon_id || coupon.id || '-'}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>바코드</div>
        <div className={styles.detailValue}>{coupon.barcode || '-'}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>할인 방식</div>
        <div className={styles.detailValue}>{DISCOUNT_TYPE_LABELS[coupon.discount_type] || '-'}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>금액/비율</div>
        <div className={styles.detailValue}>{formatCouponAmount(coupon)}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>사용 범위</div>
        <div className={styles.detailValue}>{SCOPE_TYPE_LABELS[coupon.scope_type] || '-'}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>만료일</div>
        <div className={styles.detailValue}>
          {coupon.expiry_date ? new Date(coupon.expiry_date).toLocaleDateString('ko-KR') : '-'}
        </div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>타겟 성별</div>
        <div className={styles.detailValue}>{GENDER_LABELS[coupon.target_gender] || '전체'}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>타겟 연령대</div>
        <div className={styles.detailValue}>{formatTargetAges(coupon.target_ages)}</div>
      </div>
      {coupon.description && (
        <div className={`${styles.detailItem} ${styles.fullWidth}`}>
          <div className={styles.detailLabel}>설명</div>
          <div className={styles.detailValue}>{coupon.description}</div>
        </div>
      )}
    </div>
  );

  const renderPointModal = (item) => (
    <div className={styles.detailGrid}>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>거래 유형</div>
        <div className={styles.detailValue}>{POINT_TYPE_CONFIG[item.transaction_type]?.label || item.transaction_type}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>금액</div>
        <div className={styles.detailValue}>{formatAmount(item.amount || 0)}P</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>일시</div>
        <div className={styles.detailValue}>{item.created_at ? new Date(item.created_at).toLocaleString('ko-KR') : '-'}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>승인번호</div>
        <div className={styles.detailValue}>{item.approval_number || '-'}</div>
      </div>
      {item.description && (
        <div className={`${styles.detailItem} ${styles.fullWidth}`}>
          <div className={styles.detailLabel}>설명</div>
          <div className={styles.detailValue}>{item.description}</div>
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.pageContainer}>
      {/* Summary Cards (3 horizontal) */}
      <SummaryCards items={kpiItems} />

      {/* Main Table Card */}
      <div className={styles.mainCard}>
        {/* Search Bar */}
        <div className={styles.searchRow}>
          <div className={styles.searchInputWrap}>
            <Search className={styles.searchIcon} size={20} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder={eventType === 'coupon' ? '설명, 대상으로 검색' : '설명으로 검색'}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            {/* Figma close_small — 검색어 지우기 */}
            <button type="button" className={styles.searchClear} aria-label="검색어 지우기" onClick={() => { setSearchInput(''); setSearchQuery(''); }}><X size={16} /></button>
          </div>
          <button className={styles.searchButton} onClick={handleSearch}>
            검색
          </button>
          <div className={styles.searchHint}>
            <Info className={styles.searchHintIcon} />
            <span>조회기간 내 데이터만 검색됩니다</span>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filterArea}>
          {/* Date range + period pills */}
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>조회기간</span>
            <div className={styles.dateRangeWrapper} ref={isCalendarOpen ? calendarRef : null}>
              <div className={styles.dateInputGroup} onClick={() => { setCalendarDraft(dateRange); setIsCalendarOpen(prev => !prev); }}>
                <span className={styles.dateDisplay}>{fmtDate(dateRange.start)}</span>
                <span className={styles.dateArrow}>~</span>
                <span className={styles.dateDisplay}>{fmtDate(dateRange.end)}</span>
              </div>
              {isCalendarOpen && (
                <div className={styles.calendarDropdown}>
                  <Calendar
                    mode="range"
                    variant="event"
                    value={calendarDraft}
                    onChange={setCalendarDraft}
                    onClear={() => setCalendarDraft({ start: null, end: null })}
                    onClose={() => setIsCalendarOpen(false)}
                    onConfirm={() => {
                      if (calendarDraft?.start && calendarDraft?.end) handleCalendarChange(calendarDraft);
                    }}
                  />
                </div>
              )}
            </div>
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.key}
                className={`${styles.pill} ${periodFilter === opt.key ? styles.pillActive : ''}`}
                onClick={() => handlePeriodChange(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Vertical divider */}
          <div className={styles.filterDivider} />

          {/* Type pills */}
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>유형</span>
            {currentSubTabs.map(tab => (
              <button
                key={tab.key}
                className={`${styles.pill} ${subFilter === tab.key ? styles.pillActive : ''}`}
                onClick={() => setSubFilter(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableBox}>
          {eventType === 'coupon' ? renderCouponTable() : renderPointTable()}
        </div>

        {/* Pagination */}
        <div className={styles.paginationBar}>
          <div className={styles.paginationInfo}>
            검색결과 {currentFilteredData.length}개
          </div>
          <div className={styles.paginationButtons}>
            <button
              className={styles.pageBtn}
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              &#8249;
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`${styles.pageBtn} ${currentPage === pageNum ? styles.activePage : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className={styles.pageBtn}
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              &#8250;
            </button>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <div className={styles.modalOverlay} onClick={() => setSelectedItem(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{selectedItem.type === 'coupon' ? '쿠폰 상세' : '지급 상세'}</h2>
              <button className={styles.closeButton} onClick={() => setSelectedItem(null)}>
                <X size={20} />
              </button>
            </div>
            {selectedItem.type === 'coupon'
              ? renderCouponModal(selectedItem.data)
              : renderPointModal(selectedItem.data)
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDistributions;
